'use client';

import { ArrowDown } from 'lucide-react';
import { useTokenInfo } from '../../../hooks/useTokenInfo';
import { getTokenDisplayName } from '../../../lib/utils';
import { formatUsdValue } from '../../../utils/numberFormatting';
import NetworkBadge from './NetworkBadge';
import TokenImage from './TokenImage';
import { TxButtons } from './TxButtons';
import { SwapComponentProps } from './types';

export default function Swap({ data, tx }: SwapComponentProps) {
  const { network } = data;

  const { data: enrichedData, loading } = useTokenInfo(
    data,
    tx.chainId.toString(),
    data.type,
  );

  // Use enriched data if available, fallback to original data
  const displayData = enrichedData || data;
  const { tokenIn: enrichedTokenIn, tokenOut: enrichedTokenOut } = displayData;

  // Debug: Log potential issues with token values
  if (enrichedTokenIn && enrichedTokenOut && !loading) {
    const tokenInValue = enrichedTokenIn.usdValue;
    const tokenOutValue = enrichedTokenOut.usdValue;

    if (tokenInValue && tokenOutValue) {
      const ratio = tokenInValue / tokenOutValue;
      if (ratio > 10000 || ratio < 0.0001) {
        console.warn(
          `Very suspicious swap ratio detected in UI: ${enrichedTokenIn.symbol} ($${tokenInValue}) to ${enrichedTokenOut.symbol} ($${tokenOutValue}). Ratio: ${ratio.toFixed(8)}`,
        );
      } else if (ratio > 100 || ratio < 0.01) {
        console.info(
          `Notable swap ratio in UI: ${enrichedTokenIn.symbol} ($${tokenInValue}) to ${enrichedTokenOut.symbol} ($${tokenOutValue}). Ratio: ${ratio.toFixed(6)} - This may be normal for tokens with very different prices`,
        );
      }
    }
  }

  const tokenInName = getTokenDisplayName(enrichedTokenIn);
  const tokenOutName = getTokenDisplayName(enrichedTokenOut);

  return (
    <div className="bitte-w-full bitte-max-w-md bitte-h-auto bitte-max-h-full bitte-flex bitte-flex-col bitte-gap-3 bitte-mx-auto bitte-bg-black/80 bitte-rounded-lg bitte-p-4 bitte-border bitte-border-gray-800 bitte-animate-in bitte-fade-in-0 bitte-slide-in-from-top-4 bitte-duration-300">
      <div className="bitte-flex">
        <NetworkBadge network={network} iconOverride={network?.icon} />
      </div>

      <div className="bitte-px-4 bitte-py-3 bitte-bg-[#1a1a1a] bitte-rounded bitte-flex-shrink-0 bitte-transition-all bitte-duration-200">
        <div className="bitte-text-[#5D5A5A] bitte-text-[10px] bitte-mb-1 bitte-sm:bitte-mb-2">
          SELL
        </div>
        <div className="bitte-flex bitte-justify-between bitte-items-center">
          <div>
            <div className="bitte-text-white bitte-text-2xl bitte-font-medium bitte-transition-all bitte-duration-200">
              {enrichedTokenIn?.amount || '0'}
            </div>
            <div className="bitte-text-[#858585] bitte-text-sm bitte-mt-1 bitte-transition-all bitte-duration-200">
              {loading &&
              (enrichedTokenIn?.usdValue === undefined ||
                enrichedTokenIn?.usdValue === null) ? (
                <span className="bitte-text-[#666] bitte-text-xs bitte-animate-pulse">
                  Loading price...
                </span>
              ) : enrichedTokenIn?.usdValue ? (
                <span>~ {formatUsdValue(enrichedTokenIn?.usdValue)}</span>
              ) : null}
            </div>
          </div>
          <div className="bitte-flex bitte-items-center bitte-gap-2 bitte-sm:bitte-gap-3 bitte-items-center">
            <span className="bitte-text-white bitte-text-sm bitte-font-semibold">
              {tokenInName}
            </span>
            <div>
              {loading && !enrichedTokenIn?.icon ? (
                <div className="bitte-h-6 bitte-w-6 bitte-bg-gray-600 bitte-rounded-full bitte-animate-pulse" />
              ) : (
                <TokenImage
                  src={enrichedTokenIn?.icon}
                  alt={tokenInName}
                  symbol={enrichedTokenIn?.symbol}
                  className="bitte-h-6 bitte-w-6 bitte-rounded-full bitte-transition-all bitte-duration-200"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bitte-py-2 bitte-bg-[#1a1a1a] bitte-rounded bitte-flex-shrink-0 bitte-flex bitte-justify-center bitte-transition-all bitte-duration-200">
        <ArrowDown
          className="bitte-text-[#3b82f6] bitte-transition-all bitte-duration-200"
          size={24}
        />
      </div>

      <div className="bitte-px-4 bitte-py-3 bitte-bg-[#1a1a1a] bitte-rounded bitte-flex-shrink-0 bitte-transition-all bitte-duration-200">
        <div className="bitte-text-[#5D5A5A] bitte-text-[10px] bitte-mb-1 bitte-sm:bitte-mb-2">
          BUY
        </div>
        <div className="bitte-flex bitte-justify-between bitte-items-center">
          <div>
            <div className="bitte-text-white bitte-text-2xl bitte-font-medium bitte-transition-all bitte-duration-200">
              {enrichedTokenOut?.amount || '0'}
            </div>
            <div className="bitte-flex bitte-items-center bitte-gap-1 bitte-sm:bitte-gap-2 bitte-mt-1">
              <span className="bitte-text-[#858585] bitte-text-sm bitte-transition-all bitte-duration-200">
                {loading &&
                (enrichedTokenOut?.usdValue === undefined ||
                  enrichedTokenOut?.usdValue === null) ? (
                  <span className="bitte-text-[#666] bitte-text-xs bitte-animate-pulse">
                    Loading price...
                  </span>
                ) : enrichedTokenOut?.usdValue ? (
                  <span>~ {formatUsdValue(enrichedTokenOut?.usdValue)}</span>
                ) : null}
              </span>
            </div>
          </div>
          <div className="bitte-flex bitte-items-center bitte-gap-2 bitte-sm:bitte-gap-3 bitte-items-center">
            <span className="bitte-text-white bitte-text-sm bitte-font-semibold bitte-transition-all bitte-duration-200">
              {tokenOutName}
            </span>
            <div className="bitte-transition-all bitte-duration-200">
              {loading && !enrichedTokenOut?.icon ? (
                <div className="bitte-h-6 bitte-w-6 bitte-bg-gray-600 bitte-rounded-full bitte-animate-pulse" />
              ) : (
                <TokenImage
                  src={enrichedTokenOut?.icon}
                  alt={tokenOutName}
                  symbol={enrichedTokenOut?.symbol}
                  className="bitte-h-6 bitte-w-6 bitte-rounded-full bitte-transition-all bitte-duration-200"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bitte-transition-all bitte-duration-200">
        <TxButtons tx={tx} />
      </div>
    </div>
  );
}
